<script>
import { required } from "@vuelidate/validators";

import { zipCode } from "@/utils/form-validators";
import FormRadio from "@/components/molecules/form-radio";
import Divider from "@/components/atoms/divider";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import Button from "@/components/atoms/button";
import Titre from "@/components/atoms/titre";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";

export default {
  name: "CommandeGenererFacture",
  components: { FormRadio, Divider, FormInput, FormGroup, Button, Titre },
  props: {
    client: {
      type: Object,
      required: true
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { v$, getState, getErrorMessage, checkValidation } =
      useFormValidation(props);
    return {
      v$,
      getState,
      getErrorMessage,
      checkValidation
    };
  },
  data() {
    return {
      selectedAddress: 0,
      adresse: {
        nom: "",
        prenom: "",
        nom_complément: "",
        adresse: "",
        code_postal: "",
        ville: "",
        pays: "",
        tva_intracom: ""
      }
    };
  },
  validations() {
    return {
      adresse: {
        nom: {
          required
        },
        prenom: {
          required
        },
        adresse: {
          required
        },
        code_postal: {
          required,
          zipCode
        },
        ville: {
          required
        }
      }
    };
  },
  methods: {
    selectAddress(value) {
      this.selectedAddress = value;
    },
    save() {
      if (this.selectedAddress === 0) {
        this.$emit("save", {
          useAdresseInAccount: true
        });
      } else if (this.checkValidation(this.v$)) {
        this.$emit("save", {
          useAdresseInAccount: false,
          adresse: this.adresse
        });
      }
    }
  }
};
</script>

<template>
  <div class="c-commande-generer-facture">
    <Titre
      tag="h1"
      variant="primary"
      size="md"
      class="u-mb-md u-text-center"
    >
      Générer une facture
    </Titre>
    <p class="u-mb-sm u-text-center u-text-left@md u-font-14">
      Facture à établir à l'ordre de :
    </p>

    <FormGroup size="full">
      <FormRadio
        v-model="selectedAddress"
        name="address"
        :value="0"
        block
      >
        <span class="c-commande-generer-facture__radio-label u-font-15">
          <span>
            <strong class="u-font-500">
              Vous-même
            </strong>
            <br>
            <span class="u-font-400">
              {{ client.attributes.prenom }} {{ client.attributes.nom }}
              <br>
              {{ client.attributes.adresse }}
              <br>
              {{ client.attributes.cp }} {{ client.attributes.ville }}
            </span>
          </span>
        </span>
      </FormRadio>
    </FormGroup>

    <Divider
      dotted
      class="u-mt-md u-mb-md"
    />

    <FormGroup size="full">
      <FormRadio
        v-model="selectedAddress"
        name="address"
        block
        :value="1"
      >
        <span class="c-commande-generer-facture__radio-label">
          <strong class="u-font-500">Autres coordonnées</strong>
        </span>
      </FormRadio>
    </FormGroup>

    <div
      v-if="selectedAddress === 1"
      class="c-commande-generer-facture__form-address"
    >
      <FormGroup
        :state="getState(v$,'nom', 'adresse')"
        size="full"
        class="u-mb-sm"
      >
        <FormInput
          v-model.trim="adresse.nom"
          :state="getState(v$,'nom', 'adresse')"
          size="full"
          @blur="v$.adresse.nom.$touch()"
          @input="v$.adresse.nom.$reset()"
        >
          Nom du client facturé*
        </FormInput>
        <template #message>
          {{ getErrorMessage("nom","Merci de renseigner votre nom") }}
        </template>
      </FormGroup>
      <FormGroup
        :state="getState(v$,'prenom', 'adresse')"
        size="full"
        class="u-mb-sm"
      >
        <FormInput
          v-model.trim="adresse.prenom"
          :state="getState(v$,'prenom', 'adresse')"
          size="full"
          @blur="v$.adresse.prenom.$touch()"
          @input="v$.adresse.prenom.$reset()"
        >
          Prénom du client facturé*
        </FormInput>
        <template #message>
          {{ getErrorMessage("prenom","Merci de renseigner votre prénom") }}
        </template>
      </FormGroup>
      <FormGroup
        :state="getState(v$,'adresse', 'adresse')"
        size="full"
        class="u-mb-sm"
      >
        <FormInput
          v-model.trim="adresse.adresse"
          :state="getState(v$,'adresse', 'adresse')"
          size="full"
          @blur="v$.adresse.adresse.$touch()"
          @input="v$.adresse.adresse.$reset()"
        >
          Adresse*
        </FormInput>
        <template #message>
          {{ getErrorMessage("adresse","Merci de renseigner votre adresse") }}
        </template>
      </FormGroup>
      <FormGroup
        :state="getState(v$,'code_postal', 'adresse')"
        size="full"
        class="u-mb-sm"
      >
        <FormInput
          v-model.trim="adresse.code_postal"
          :state="getState(v$,'code_postal', 'adresse')"
          size="xs"
          pattern="[0-9]*"
          @blur="v$.adresse.code_postal.$touch()"
          @input="v$.adresse.code_postal.$reset()"
        >
          Code postal*
        </FormInput>
        <template #message>
          <p>
            <template v-if="v$.adresse.code_postal.required.$invalid">
              Merci de renseigner votre code postal
            </template>
            <template v-else>
              {{ getErrorMessage("code_postal","Ce code postal est invalide") }}
            </template>
          </p>
        </template>
      </FormGroup>
      <FormGroup
        :state="getState(v$,'ville', 'adresse')"
        size="full"
        class="u-mb-sm"
      >
        <FormInput
          v-model.trim="adresse.ville"
          :state="getState(v$,'ville', 'adresse')"
          size="full"
          @blur="v$.adresse.ville.$touch()"
          @input="v$.adresse.ville.$reset()"
        >
          Ville*
        </FormInput>
        <template #message>
          {{ getErrorMessage("ville","Merci de renseigner votre ville") }}
        </template>
      </FormGroup>
      <FormGroup
        :state="getState(v$,'pays')"
        size="full"
        class="u-mb-sm"
      >
        <FormInput
          :state="getState(v$,'pays')"
          size="full"
        >
          Pays
        </FormInput>
        <template #message>
          {{ getErrorMessage("pays") }}
        </template>
      </FormGroup>
      <FormGroup
        :state="getState(v$,'tva_intracom')"
        size="full"
        class="u-mb-sm"
      >
        <FormInput
          :state="getState(v$,'tva_intracom')"
          size="full"
        >
          Numéro de TVA intracommunautaire
        </FormInput>
        <template #message>
          {{ getErrorMessage("tva_intracom") }}
        </template>
      </FormGroup>
      <p class="c-commande-generer-facture__field-required u-font-11">
        *champs obligatoires
      </p>
    </div>

    <div class="u-text-center u-mt-md">
      <Button
        variant="primary"
        radius="full"
        size="md"
        @click="save()"
      >
        Valider l'adresse de facturation
      </Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-commande-generer-facture {
  .c-commande-generer-facture__form-address {
    border: 2px solid color(primary-light);
    border-top: 0;
    border-radius: 0 0 radius(md) radius(md);
    position: relative;
    padding: 0 spacing(md) spacing(md) spacing(md);
    &:before {
      content: "";
      display: block;
      background-color: #fff;
      height: 5px;
      position: absolute;
      top: -5px;
      left: -2px;
      right: -2px;
      z-index: zIndex(above);
      border-left: 2px solid color(primary-light);
      border-right: 2px solid color(primary-light);
    }
  }

  .c-commande-generer-facture__radio-label {
    min-height: 8rem;
    display: flex;
    align-items: center;
  }
  .c-commande-generer-facture__field-required {
    color: #676767;
  }
}
</style>
